import React, { useState, useMemo } from 'react';
import pb from '../../services/pocketbase';

function UserManager({ users, onUserUpdated }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Debug: Log des données utilisateurs reçues
  console.log('UserManager - Données utilisateurs reçues:', users);
  users.forEach((user, index) => {
    console.log(`Utilisateur ${index + 1}:`, {
      id: user.id,
      name: user.name,
      username: user.username,
      email: user.email,
      emailVisibility: user.emailVisibility,
      role: user.role
    });
  });

  // Statistiques des utilisateurs
  const userStats = useMemo(() => {
    const total = users.length;
    const admins = users.filter(u => u.role === 'admin').length;
    const regularUsers = users.filter(u => u.role === 'user' || !u.role).length;
    const verified = users.filter(u => u.verified).length;
    const thisMonth = users.filter(u => {
      const userDate = new Date(u.created);
      const now = new Date();
      return userDate.getMonth() === now.getMonth() && userDate.getFullYear() === now.getFullYear();
    }).length;

    return { total, admins, regularUsers, verified, thisMonth };
  }, [users]);

  // Filtrage et tri des utilisateurs
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(user => {
      const matchesSearch = 
        (user.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.username?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesRole = roleFilter === 'all' || 
        (roleFilter === 'admin' && user.role === 'admin') ||
        (roleFilter === 'user' && (user.role === 'user' || !user.role));
      
      const matchesVerified = verifiedFilter === 'all' ||
        (verifiedFilter === 'verified' && user.verified) ||
        (verifiedFilter === 'unverified' && !user.verified);

      return matchesSearch && matchesRole && matchesVerified;
    });

    // Tri
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name || a.username || '';
          bValue = b.name || b.username || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'role':
          aValue = a.role || 'user';
          bValue = b.role || 'user';
          break;
        case 'created':
        default:
          aValue = new Date(a.created);
          bValue = new Date(b.created);
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, searchTerm, roleFilter, verifiedFilter, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
  const paginatedUsers = filteredAndSortedUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleRoleChange = async (userId, newRole) => {
    try {
      await pb.collection('users').update(userId, { role: newRole });
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du rôle:', error);
      alert('Erreur lors de la mise à jour du rôle');
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement l'utilisateur "${user?.name || user?.email}" ?\n\nCette action supprimera TOUTES ses données liées :\n- Messages de chat\n- Avis/commentaires\n- Articles du panier\n- Favoris\n\nCette action est IRRÉVERSIBLE.`)) return;
    
    try {
      // 1. Supprimer les messages de chat de l'utilisateur
      try {
        const userMessages = await pb.collection('chat_messages').getFullList({
          filter: `sender = "${userId}"`
        });
        for (const message of userMessages) {
          await pb.collection('chat_messages').delete(message.id);
        }
        console.log(`${userMessages.length} messages supprimés`);
      } catch (error) {
        console.log('Pas de messages à supprimer:', error);
      }

      // 2. Supprimer ou modifier les chats où l'utilisateur est participant
      try {
        const userChats = await pb.collection('chats').getFullList({
          filter: `user = "${userId}" || admin = "${userId}" || participants = "${userId}"`
        });
        for (const chat of userChats) {
          await pb.collection('chats').delete(chat.id);
        }
        console.log(`${userChats.length} chats supprimés`);
      } catch (error) {
        console.log('Pas de chats à supprimer:', error);
      }

      // 3. Supprimer les avis de l'utilisateur
      try {
        const userReviews = await pb.collection('reviews').getFullList({
          filter: `user = "${userId}"`
        });
        for (const review of userReviews) {
          await pb.collection('reviews').delete(review.id);
        }
        console.log(`${userReviews.length} avis supprimés`);
      } catch (error) {
        console.log('Pas d\'avis à supprimer:', error);
      }

      // 4. Supprimer les articles du panier
      try {
        const cartItems = await pb.collection('cart_items').getFullList({
          filter: `user = "${userId}"`
        });
        for (const item of cartItems) {
          await pb.collection('cart_items').delete(item.id);
        }
        console.log(`${cartItems.length} articles du panier supprimés`);
      } catch (error) {
        console.log('Pas d\'articles du panier à supprimer:', error);
      }

      // 5. Supprimer les favoris
      try {
        const favorites = await pb.collection('user_favorites').getFullList({
          filter: `user = "${userId}"`
        });
        for (const favorite of favorites) {
          await pb.collection('user_favorites').delete(favorite.id);
        }
        console.log(`${favorites.length} favoris supprimés`);
      } catch (error) {
        console.log('Pas de favoris à supprimer:', error);
      }

      // 6. Supprimer les données d'authentification système
      try {
        const authOrigins = await pb.collection('_authOrigins').getFullList({
          filter: `recordRef = "${userId}"`
        });
        for (const origin of authOrigins) {
          await pb.collection('_authOrigins').delete(origin.id);
        }
        console.log(`${authOrigins.length} origines d'auth supprimées`);
      } catch (error) {
        console.log('Pas d\'origines d\'auth à supprimer:', error);
      }

      try {
        const externalAuths = await pb.collection('_externalAuths').getFullList({
          filter: `recordRef = "${userId}"`
        });
        for (const auth of externalAuths) {
          await pb.collection('_externalAuths').delete(auth.id);
        }
        console.log(`${externalAuths.length} auth externes supprimées`);
      } catch (error) {
        console.log('Pas d\'auth externes à supprimer:', error);
      }

      try {
        const mfas = await pb.collection('_mfas').getFullList({
          filter: `recordRef = "${userId}"`
        });
        for (const mfa of mfas) {
          await pb.collection('_mfas').delete(mfa.id);
        }
        console.log(`${mfas.length} MFA supprimées`);
      } catch (error) {
        console.log('Pas de MFA à supprimer:', error);
      }

      try {
        const otps = await pb.collection('_otps').getFullList({
          filter: `recordRef = "${userId}"`
        });
        for (const otp of otps) {
          await pb.collection('_otps').delete(otp.id);
        }
        console.log(`${otps.length} OTP supprimées`);
      } catch (error) {
        console.log('Pas d\'OTP à supprimer:', error);
      }

      // 7. Enfin, supprimer l'utilisateur
      await pb.collection('users').delete(userId);
      
      if (onUserUpdated) onUserUpdated();
      alert('Utilisateur et toutes ses données supprimés avec succès !');
      
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(`Erreur lors de la suppression: ${error.message}\n\nVérifiez la console pour plus de détails.`);
    }
  };

  const handleToggleEmailVisibility = async (userId, currentVisibility) => {
    try {
      await pb.collection('users').update(userId, { 
        emailVisibility: !currentVisibility 
      });
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la visibilité:', error);
      alert('Erreur lors de la mise à jour de la visibilité de l\'email');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="user-manager">
      <h4>Gestion des Utilisateurs</h4>
      
      {/* Statistiques */}
      <div className="user-stats">
        <div className="stat-card">
          <h5>Total</h5>
          <span className="stat-number">{userStats.total}</span>
        </div>
        <div className="stat-card">
          <h5>Admins</h5>
          <span className="stat-number admin-count">{userStats.admins}</span>
        </div>
        <div className="stat-card">
          <h5>Utilisateurs</h5>
          <span className="stat-number">{userStats.regularUsers}</span>
        </div>
        <div className="stat-card">
          <h5>Vérifiés</h5>
          <span className="stat-number verified-count">{userStats.verified}</span>
        </div>
        <div className="stat-card">
          <h5>Ce mois</h5>
          <span className="stat-number new-count">{userStats.thisMonth}</span>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="user-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Rechercher par nom, email ou pseudo..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tous les rôles</option>
            <option value="user">Utilisateurs</option>
            <option value="admin">Administrateurs</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            value={verifiedFilter}
            onChange={(e) => {
              setVerifiedFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tous les statuts</option>
            <option value="verified">Vérifiés</option>
            <option value="unverified">Non vérifiés</option>
          </select>
        </div>
      </div>

      {/* Tableau des utilisateurs */}
      <div className="dashboard-table-container">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Avatar</th>
              <th onClick={() => handleSort('name')} style={{cursor: 'pointer'}}>
                Nom/Pseudo {getSortIcon('name')}
              </th>
              <th onClick={() => handleSort('email')} style={{cursor: 'pointer'}}>
                Email {getSortIcon('email')}
              </th>
              <th>Téléphone</th>
              <th>Code Postal</th>
              <th onClick={() => handleSort('role')} style={{cursor: 'pointer'}}>
                Rôle {getSortIcon('role')}
              </th>
              <th>Statut</th>
              <th onClick={() => handleSort('created')} style={{cursor: 'pointer'}}>
                Inscription {getSortIcon('created')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length > 0 ? paginatedUsers.map(user => (
              <tr key={user.id}>
                <td>
                  {user.avatar ? (
                    <img src={pb.files.getURL(user, user.avatar)} alt={user.name} className="table-thumbnail" />
                  ) : (
                    <div className="table-thumbnail-placeholder">
                      {(user.name || user.username || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                </td>
                <td>
                  <div>
                    <strong>{user.name || 'N/A'}</strong>
                    {user.username && <div className="username">@{user.username}</div>}
                  </div>
                </td>
                <td>
                  <div className="email-cell">
                    {user.email ? (
                      <div>
                        <a href={`mailto:${user.email}`} className="email-link">
                          {user.email}
                        </a>
                        {!user.emailVisibility && (
                          <div className="visibility-warning">
                            ⚠️ Email masqué par l'utilisateur
                            <button 
                              onClick={() => handleToggleEmailVisibility(user.id, user.emailVisibility)}
                              className="toggle-visibility-btn"
                              title="Rendre l'email visible"
                            >
                              👁️ Rendre visible
                            </button>
                          </div>
                        )}
                      </div>
                    ) : user.emailVisibility === false ? (
                      <div className="email-hidden">
                        <span className="hidden-email-text">Email masqué</span>
                        <div className="visibility-warning">
                          ⚠️ L'utilisateur a masqué son email
                          <button 
                            onClick={() => handleToggleEmailVisibility(user.id, user.emailVisibility)}
                            className="toggle-visibility-btn"
                            title="Rendre l'email visible"
                          >
                            👁️ Rendre visible
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className="no-email">Aucun email</span>
                    )}
                  </div>
                </td>
                <td>{user.phone_number || 'N/A'}</td>
                <td>{user.postal_code || 'N/A'}</td>
                <td>
                  <select
                    value={user.role || 'user'}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className={`role-badge ${user.role === 'admin' ? 'admin' : 'user'}`}
                  >
                    <option value="user">Utilisateur</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  <div className="status-badges">
                    <span className={`status-badge ${user.verified ? 'verified' : 'unverified'}`}>
                      {user.verified ? '✓ Vérifié' : '⚠ Non vérifié'}
                    </span>
                  </div>
                </td>
                <td>{new Date(user.created).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="delete-btn"
                      title="Supprimer l'utilisateur"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="9">Aucun utilisateur trouvé.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Précédent
          </button>
          
          <span className="page-info">
            Page {currentPage} sur {totalPages} ({filteredAndSortedUsers.length} utilisateurs)
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
}

export default UserManager;
